import React, { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Tooltip, XAxis, YAxis } from "recharts";
import { Scale, Timer, Trophy, Weight } from "lucide-react";
import { ChartResponsiveContainer } from "../../../components/ChartResponsiveContainer";
import { COLORS } from "../../../constants";
import {
  CRITERIO_PONTUACAO,
  formatNota,
  type PontuacaoFuncionario,
} from "./pontuacaoFuncionarios";

const TOP_CHART = 10;
const COR_PONTOS = COLORS.PRIMARY;
const COR_QTD = COLORS.WARNING_DARK;
const COR_VEL = COLORS.TEAL_DARK;

type PontuacaoTabProps = {
  ranking: PontuacaoFuncionario[];
  undNome: string;
  onSelectUsuario: (userId: number) => void;
};

function truncar(nome: string, max = 16): string {
  const t = nome.trim();
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
}

function BarraNota({
  label,
  valor,
  cor,
}: {
  label: string;
  valor: number;
  cor: string;
}) {
  return (
    <div className="min-w-0">
      <div className="mb-0.5 flex items-center justify-between gap-2 text-[11px] text-neutral-500">
        <span>{label}</span>
        <span className="tabular-nums font-semibold text-neutral-700">
          {formatNota(valor)}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-neutral-100">
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.min(100, Math.max(0, valor * 10))}%`,
            background: cor,
          }}
        />
      </div>
    </div>
  );
}

const PontuacaoTab: React.FC<PontuacaoTabProps> = ({
  ranking,
  undNome,
  onSelectUsuario,
}) => {
  const chartData = useMemo(
    () =>
      ranking
        .filter((u) => u.pontuacao > 0)
        .slice(0, TOP_CHART)
        .map((u) => ({
          name: truncar(u.userNome),
          fullName: u.userNome,
          pontos: u.pontuacao,
          qtd: u.pontuacaoQtd,
          vel: u.pontuacaoTempo,
        })),
    [ranking]
  );

  const comPontos = ranking.filter((u) => u.pontuacao > 0).length;
  const lider = ranking[0];

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-neutral-200 bg-white p-4 sm:p-5">
        <p className="mb-1 flex items-center gap-2 text-sm font-semibold text-neutral-900">
          <Scale className="h-4 w-4 text-primary" />
          Como funciona a nota
        </p>
        <p className="mb-4 text-xs text-neutral-500">{CRITERIO_PONTUACAO}</p>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-lg border border-orange-100 bg-orange-50 p-3">
            <p className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-orange-800">
              <Weight className="h-4 w-4" />
              Produção
            </p>
            <p className="text-xs leading-relaxed text-orange-900/80">
              Quem mais produziu em <strong>{undNome}</strong> naquele dia ganha{" "}
              <strong>10</strong>. Fez metade do líder do dia? Nota{" "}
              <strong>5</strong>.
            </p>
          </div>
          <div className="rounded-lg border border-teal-100 bg-teal-50 p-3">
            <p className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-teal-800">
              <Timer className="h-4 w-4" />
              Rapidez
            </p>
            <p className="text-xs leading-relaxed text-teal-900/80">
              Quem teve o menor tempo médio <strong>do dia</strong> ganha{" "}
              <strong>10</strong>. Demorou o dobro? Nota <strong>5</strong>.
            </p>
          </div>
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
            <p className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-primary">
              <Trophy className="h-4 w-4" />
              Média do período
            </p>
            <p className="text-xs leading-relaxed text-neutral-700">
              Cada dia tem uma nota (média de produção e rapidez). No período
              filtrado, aparece a <strong>média dos dias</strong>. Ex.: 10, 8,
              7 e 5 → <strong>7,5</strong>.
            </p>
          </div>
        </div>
      </section>

      {chartData.length > 0 && (
        <section className="rounded-lg border border-neutral-200 bg-white p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-neutral-900">
            Ranking de notas
          </h3>
          <p className="mt-0.5 text-xs text-neutral-500">
            Top {chartData.length} do período
            {lider?.pontuacao
              ? ` · líder ${lider.userNome} com média ${formatNota(lider.pontuacao)}`
              : ""}
            {comPontos > 0 ? ` · ${comPontos} ${comPontos === 1 ? "pessoa com nota" : "pessoas com nota"}` : ""}
            .
          </p>
          <div className="relative mt-4 h-[18rem] w-full min-w-0 overflow-hidden sm:h-[20rem]">
            <ChartResponsiveContainer>
              <BarChart
                data={chartData}
                margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10 }}
                  interval={0}
                  angle={-28}
                  textAnchor="end"
                  height={48}
                />
                <YAxis
                  domain={[0, 10]}
                  tick={{ fontSize: 11 }}
                  width={36}
                />
                <Tooltip
                  formatter={(value, name) => [
                    formatNota(Number(value ?? 0)),
                    name === "pontos"
                      ? "Nota"
                      : name === "qtd"
                        ? "Produção"
                        : "Rapidez",
                  ]}
                  labelFormatter={(_, payload) =>
                    payload?.[0]?.payload?.fullName ?? ""
                  }
                />
                <Bar dataKey="pontos" name="pontos" radius={[4, 4, 0, 0]}>
                  {chartData.map((row, index) => (
                    <Cell
                      key={row.fullName}
                      fill={index === 0 ? COR_PONTOS : COR_VEL}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ChartResponsiveContainer>
          </div>
        </section>
      )}

      <section className="space-y-2">
        {ranking.length === 0 ? (
          <div className="rounded-lg border border-neutral-200 bg-white px-3 py-6 text-center text-sm text-neutral-500">
            Nenhum usuário com tempo no período para os filtros.
          </div>
        ) : (
          ranking.map((row) => {
            const isFirst = row.posicao === 1 && row.pontuacao > 0;
            const isPodium = row.posicao <= 3 && row.pontuacao > 0;
            return (
              <button
                key={row.userId}
                type="button"
                onClick={() => onSelectUsuario(row.userId)}
                className={`flex w-full flex-col gap-3 rounded-lg border px-4 py-3 text-left transition hover:border-primary/40 hover:bg-neutral-50 sm:flex-row sm:items-center sm:justify-between ${
                  isFirst
                    ? "border-primary/30 bg-primary/5"
                    : "border-neutral-200 bg-white"
                }`}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                      isFirst
                        ? "bg-primary text-white"
                        : isPodium
                          ? "bg-neutral-800 text-white"
                          : "bg-neutral-100 text-neutral-700"
                    }`}
                  >
                    {isPodium ? (
                      <Trophy className="h-4 w-4" />
                    ) : (
                      row.posicao
                    )}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-neutral-900">
                      {row.userNome}
                      {isFirst ? (
                        <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">
                          Líder
                        </span>
                      ) : null}
                    </p>
                    <p className="break-words text-xs text-neutral-500">
                      {row.diasComNota > 0
                        ? `média de ${row.diasComNota} ${
                            row.diasComNota === 1 ? "dia" : "dias"
                          }${
                            row.notasDias.length
                              ? ` (${row.notasDias.map((n) => formatNota(n)).join(", ")})`
                              : ""
                          }`
                        : `${row.producaoFormatada} · médio ${row.tempoMedioFormatado}`}
                      {" · "}
                      {row.ocorrencias}{" "}
                      {row.ocorrencias === 1 ? "cronometragem" : "cronometragens"}
                    </p>
                  </div>
                </div>
                <div className="grid w-full gap-2 sm:max-w-xs sm:shrink-0">
                  <div className="flex items-baseline justify-end gap-1.5">
                    <span
                      className={`text-2xl font-black tabular-nums ${
                        isFirst ? "text-primary" : "text-neutral-900"
                      }`}
                    >
                      {formatNota(row.pontuacao)}
                    </span>
                    <span className="text-xs font-semibold text-neutral-500">
                      / 10
                    </span>
                  </div>
                  <BarraNota
                    label="Produção"
                    valor={row.pontuacaoQtd}
                    cor={COR_QTD}
                  />
                  <BarraNota
                    label="Rapidez"
                    valor={row.pontuacaoTempo}
                    cor={COR_VEL}
                  />
                </div>
              </button>
            );
          })
        )}
      </section>
    </div>
  );
};

export default PontuacaoTab;
